import { getApiV1Bootstrap } from '@/open-api/bootstrap/bootstrap';

import type { GetBootstrapResponse } from '@/open-api/model';
import type { FileFormat } from '@/types/file-format';
import type { ValidCurrency } from '@/types/plans';
import type { Settings } from '@/types/settings';
import type { VideoResolution } from '@/types/video-resolution';

export const fallbackSettings: Settings = {
  auth_token: false,
  credits: {
    credits_max: 3,
    credits_current: 0,
  },
  status: 'success',
  country: {
    id: 239,
    iso_code_2: 'US',
    vat_rate: 0,
  },
  currency: {
    id: 1,
    code: 'USD',
    symbol_left: '$',
  },
  subscriptionData: null,
  chatgpt: {
    remaining: 0,
  },
  theme_preference: -1,
  user: {
    is_authenticated: false,
  },
  preferences: {},
};

/** The API sends `null` for missing optional values; `Settings` uses `undefined`. */
const optional = <T>(value: T | null | undefined): T | undefined =>
  value ?? undefined;

/**
 * Maps the .NET bootstrap response onto the `Settings` shape the rest of the
 * site reads, which keeps the PHP `setup_frontend` field names.
 */
export const mapBootstrapToSettings = (
  bootstrap: NonNullable<GetBootstrapResponse>
): Settings => {
  const { admin, subscriptionData: subscription, user } = bootstrap;

  return {
    status: bootstrap.status,
    auth_token: bootstrap.authToken,
    theme_preference: bootstrap.themePreference,
    isPremium: bootstrap.isPremium,
    isBusiness: bootstrap.isBusiness,
    admin: admin
      ? {
          adminSubdomain: admin.adminSubdomain,
          jAdminSubdomain: admin.jAdminSubdomain,
          adminUrls: admin.adminUrls.map(
            (link) => [link?.[0] ?? '', link?.[1] ?? ''] as [string, string]
          ),
        }
      : undefined,
    chatgpt: {
      remaining: bootstrap.chatGpt.remaining,
    },
    country: {
      id: bootstrap.country.id,
      iso_code_2: bootstrap.country.isoCode2,
      vat_rate: bootstrap.country.vatRate,
    },
    // Only customers with a credit allocation get credits
    credits: bootstrap.credits
      ? {
          credits_max: bootstrap.credits.creditsMax,
          credits_current: bootstrap.credits.creditsCurrent,
        }
      : fallbackSettings.credits,
    currency: {
      id: bootstrap.currency.id,
      code: bootstrap.currency.code as ValidCurrency,
      symbol_left: bootstrap.currency.symbolLeft,
    },
    preferences: {
      file_format: bootstrap.preferences.fileFormat as FileFormat,
      video_file_format: optional(bootstrap.preferences.videoFileFormat) as
        | VideoResolution
        | undefined,
      credit_usage_acknowledged: bootstrap.preferences.creditUsageAcknowledged,
    },
    subscriptionData: subscription
      ? {
          isActive: subscription.isActive,
          isActiveOrSuspended: subscription.isActiveOrSuspended,
          isPaused: subscription.isPaused,
          isPausePending: subscription.isPausePending,
          resumeDate: subscription.resumeDate,
          period: subscription.period,
          plan: subscription.plan,
          subscriptionId: subscription.subscriptionId,
          isDowngradePending: subscription.isDowngradePending,
          downgradeEffectiveDate: subscription.downgradeEffectiveDate,
          downgradePlan: subscription.downgradePlan,
          downgradePeriod: subscription.downgradePeriod,
          paid_through_paypal: subscription.paidThroughPaypal,
          couponApplied: subscription.couponApplied,
          hasSubscription: subscription.hasSubscription,
          isFree: subscription.isFree,
          nextBillingDate: subscription.nextBillingDate,
          autoRenew: subscription.autoRenew,
          // Some consumers check the object and others its id, so no id means not overdue
          overduePayment: subscription.overduePayment?.id
            ? {
                id: subscription.overduePayment.id,
                overdueDate: subscription.overduePayment.overdueDate ?? '',
              }
            : undefined,
        }
      : null,
    user: {
      is_authenticated: user?.isAuthenticated ?? false,
      id: optional(user?.id),
      email: optional(user?.email),
      first_name: optional(user?.firstName),
      last_name: optional(user?.lastName),
      country: optional(user?.country),
      hasAccountAdmin: optional(user?.hasAccountAdmin),
      hasAccountArtist: optional(user?.hasAccountArtist),
      profile_picture: optional(user?.profilePicture),
      isGoogleUser: optional(user?.isGoogleUser),
      auth_token: optional(user?.authToken),
      isEmailVerified: optional(user?.isEmailVerified),
      referralCode: optional(user?.referralCode),
      bypassTurnstile: optional(user?.bypassTurnstile),
      download_count: optional(user?.downloadCount),
      renewed_download_date: optional(user?.renewedDownloadDate),
      hasStripeCustomer: optional(user?.hasStripeCustomer),
    },
  };
};

export const setupFrontend = async (): Promise<Settings> => {
  // Return generic information if running on the server. Real settings are
  // fetched on the client, where the auth cookies are sent with the request.
  if (typeof window === 'undefined') {
    return fallbackSettings;
  }

  const frontendVersion = `uppbeat-next@${process.env.NEXT_PUBLIC_VERSION ?? 'unknown'}`;

  // Signed-in users are identified by the authorization_token cookie, which
  // the generated client sends with credentials
  const bootstrap = await getApiV1Bootstrap({
    FrontendVersion: frontendVersion,
  });

  return bootstrap ? mapBootstrapToSettings(bootstrap) : fallbackSettings;
};
